// Clothing listing support (built for Safety Pin — 1-of-1 upcycled, screen-
// printed apparel). Clothing items use category "clothing"; the garment type is
// stored as the item's `subtype`, and the rest of the apparel attributes live in
// the flexible `attributesJson` under the shape below. Kept in one place so the
// listing form, product page, storefront filters, and server all agree.

export const CLOTHING_CATEGORY = "clothing";

export type GarmentTypeOption = { value: string; label: string };

// Value doubles as the item `subtype`. Tuned to Safety Pin's actual catalog
// (screen-printed tees, denim jackets, hoodies/crewnecks, thrifted jeans).
export const GARMENT_TYPES: GarmentTypeOption[] = [
  { value: "tee", label: "Tee" },
  { value: "hoodie", label: "Hoodie / Sweatshirt" },
  { value: "jacket", label: "Jacket" },
  { value: "pants", label: "Jeans / Pants" },
  { value: "other", label: "Other" },
];

export const CLOTHING_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "One size"];

export type ConditionOption = { value: string; label: string };

// Pre-loved bases, so condition is front-and-center. "Reworked" is the hero
// state (her whole thing is upcycled), the rest describe the garment's wear.
export const CLOTHING_CONDITIONS: ConditionOption[] = [
  { value: "reworked", label: "Reworked / upcycled" },
  { value: "excellent", label: "Excellent" },
  { value: "good", label: "Good" },
  { value: "loved", label: "Well-loved" },
];

// Trendy aesthetic tags, the way Depop/Gen-Z resale shops tag for discovery.
// Shown as #hashtag chips on listings.
export const CLOTHING_VIBES = [
  "punk",
  "grunge",
  "y2k",
  "vintage",
  "streetwear",
  "goth",
  "graphic",
  "distressed",
  "cyber",
  "cottagecore",
];

export type ClothingMeasurements = {
  chest?: string;
  length?: string;
  waist?: string;
  inseam?: string;
};

export type ClothingAttributes = {
  garmentType?: string;
  size?: string;
  condition?: string;
  color?: string;
  measurements?: ClothingMeasurements;
  vibes?: string[];
};

export function isClothingItem(item: { category?: string | null } | null | undefined): boolean {
  return String(item?.category || "").toLowerCase() === CLOTHING_CATEGORY;
}

// Pull the clothing shape out of a raw item. Reads garmentType from subtype
// (the canonical store) and the rest from attributesJson.
export function getClothingAttributes(
  item:
    | { subtype?: string | null; attributesJson?: unknown }
    | null
    | undefined,
): ClothingAttributes {
  const attrs = (item?.attributesJson ?? {}) as Record<string, unknown>;
  const measurements = (attrs.measurements ?? {}) as ClothingMeasurements;
  return {
    garmentType: item?.subtype || (attrs.garmentType as string) || undefined,
    size: (attrs.size as string) || undefined,
    condition: (attrs.condition as string) || undefined,
    color: (attrs.color as string) || undefined,
    measurements: {
      chest: measurements.chest || undefined,
      length: measurements.length || undefined,
      waist: measurements.waist || undefined,
      inseam: measurements.inseam || undefined,
    },
    vibes: Array.isArray(attrs.vibes) ? (attrs.vibes as string[]) : undefined,
  };
}

export function conditionLabel(value: string | undefined): string | undefined {
  return CLOTHING_CONDITIONS.find((c) => c.value === value)?.label;
}

export function garmentLabel(value: string | undefined): string | undefined {
  return GARMENT_TYPES.find((g) => g.value === value)?.label;
}

// The array of listing photos, tolerant of legacy single-image items. Cover is
// always first. Filters out empties so the carousel never renders blank frames.
// Sale state helpers. An item is On Sale when it has a salePrice strictly below
// its regular price. Works for any item (not just clothing).
export function saleInfo(
  item: { price?: string | number | null; salePrice?: string | number | null } | null | undefined,
): { onSale: boolean; price: number; salePrice: number | null } {
  const price = Number(item?.price ?? 0);
  const raw = item?.salePrice;
  const salePrice = raw === null || raw === undefined || raw === "" ? null : Number(raw);
  const onSale = salePrice !== null && salePrice > 0 && salePrice < price;
  return { onSale, price, salePrice: onSale ? salePrice : null };
}

export function isFeatured(item: { isFeatured?: number | null } | null | undefined): boolean {
  return Number(item?.isFeatured ?? 0) === 1;
}

export function getListingImages(
  item: { imagesJson?: unknown; imageUrl?: string | null } | null | undefined,
): string[] {
  const arr = Array.isArray(item?.imagesJson) ? (item!.imagesJson as string[]) : [];
  const cleaned = arr.filter((u) => typeof u === "string" && u.trim().length > 0);
  if (cleaned.length > 0) return cleaned;
  return item?.imageUrl ? [item.imageUrl] : [];
}
